import { useState } from 'react';
import type { Ctx } from '../ctx';
import { actionLine, fmt } from '../model';
import { CutoverList, EnvChip, Modal, StateLine } from '../ui';
import type { ProbeRequest } from '../types';

const LIVE_WORD = 'LIVE';

export default function Tools({ ctx }: { ctx: Ctx }) {
  const { data, env } = ctx;
  const empties = data.secrets.filter((s) => !s.set);
  const dialLive = data.dials?.tts.dryRun === false;

  const [form, setForm] = useState<ProbeRequest>({ requestType: 'sales', screens: '1-10', countryTag: '+1', dryRun: true });
  const [busy, setBusy] = useState(false);
  const [confirmLive, setConfirmLive] = useState(false);
  const [typed, setTyped] = useState('');
  const set = <K extends keyof ProbeRequest>(k: K, v: ProbeRequest[K]): void => setForm((f) => ({ ...f, [k]: v }));

  const send = async (): Promise<void> => {
    setConfirmLive(false);
    setTyped('');
    setBusy(true);
    try {
      const r = await data.sendProbe(form);
      ctx.toast(
        r.accepted
          ? `Probe ${r.key} accepted (${r.dryRun ? 'dry-run' : 'LIVE'}) — see Talk to Sales → Logs`
          : `Probe rejected by our route: HTTP ${r.status} (the guard did not accept our own signature)`,
      );
      window.setTimeout(() => data.reload(), 1500);
    } catch (e) {
      ctx.toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  // dryRun:false only bites when the dial is live — that is the case that needs the typed confirmation.
  const onSend = (): void => {
    if (!form.dryRun && dialLive) setConfirmLive(true);
    else void send();
  };

  return (
    <>
      <h1>
        Tools <EnvChip env={env} />
      </h1>
      <p className="sub">Things you would otherwise do with a script and a staff JWT.</p>
      <StateLine loading={data.loading} error={data.error} />

      <section className="grid g2">
        <div className="card pad">
          <h2>Send a signed test probe · Talk to Sales</h2>
          <p className="note" style={{ margin: '0 0 10px' }}>
            Builds a synthetic submission, signs it with this env's secret and POSTs it to our own route
            (raw body → signature guard → intake → ledger). Lead <code>revops-probe+…</code>, ignored by
            the shadow scripts. It cannot prove the secret equals Webflow's — only a real submission does.
          </p>
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <label>
              Request type
              <br />
              <select aria-label="probe request type" value={form.requestType} onChange={(e) => set('requestType', e.target.value as ProbeRequest['requestType'])} disabled={!ctx.admin}>
                <option value="sales">sales</option>
                <option value="support">support</option>
              </select>
            </label>
            <label>
              Screens
              <br />
              <select aria-label="probe screens" value={form.screens} onChange={(e) => set('screens', e.target.value as ProbeRequest['screens'])} disabled={!ctx.admin}>
                <option value="1-10">1-10</option>
                <option value="11-25">11-25</option>
                <option value="26-100">26-100</option>
                <option value="100">100</option>
              </select>
            </label>
            <label>
              Country tag
              <br />
              <select aria-label="probe country tag" value={form.countryTag} onChange={(e) => set('countryTag', e.target.value)} disabled={!ctx.admin}>
                <option value="+1">+1</option>
                <option value="+44">+44</option>
                <option value="+49">+49</option>
                <option value="+971">+971</option>
              </select>
            </label>
            <label>
              Dry-run
              <br />
              <select aria-label="probe dry-run" value={form.dryRun ? 'force' : 'dial'} onChange={(e) => set('dryRun', e.target.value === 'force')} disabled={!ctx.admin}>
                <option value="force">force dry-run</option>
                <option value="dial">{dialLive ? 'follow dial (LIVE)' : 'follow dial (dry-run)'}</option>
              </select>
            </label>
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <button type="button" className="btn primary" disabled={!ctx.admin || busy} title={ctx.admin ? undefined : 'admin only'} onClick={onSend}>
              {busy ? 'Sending…' : 'Send probe'}
            </button>
            <span className="note">
              {form.dryRun || !dialLive
                ? 'Records would payloads; writes nothing.'
                : 'LIVE: creates a real contact + note and enrolls it — the workflow will open a deal on a rep.'}
            </span>
          </div>
        </div>

        <div className="card pad">
          <h2>Recent actions</h2>
          <p className="note" style={{ margin: '0 0 8px' }}>
            Every write made through this console or its api — who, what, when (<code>GET /api/actions</code>).
          </p>
          {data.actions.length ? (
            <table>
              <tbody>
                {data.actions.slice(0, 12).map((a) => (
                  <tr key={a.id}>
                    <td className="mono dim" style={{ whiteSpace: 'nowrap' }}>{fmt(a.at)}</td>
                    <td className="dim" style={{ whiteSpace: 'nowrap' }}>{a.actor.split('@')[0]}</td>
                    <td className="wrap">{actionLine(a)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty">{data.loading ? 'loading…' : 'No actions recorded on this env yet.'}</div>
          )}
        </div>
      </section>

      <section className="grid g2">
        <div className="card pad">
          <h2>Secrets status</h2>
          <p className="note" style={{ margin: '0 0 8px' }}>
            Key names from <code>emit-k8s-secrets.sh {env === 'prod' ? 'prod' : 'sbx'} print-keys</code>{' '}
            vs the live k8s Secret. Values are never shown.
          </p>
          <div className="row" style={{ gap: 5 }}>
            {data.secrets.map((s) => (
              <span className={`chip mono ${s.set ? 'ok' : 'warn'}`} key={s.key}>
                {s.key}
                {s.set ? '' : ' · EMPTY'}
              </span>
            ))}
            {!data.secrets.length ? <span className="dim">no key list from the api</span> : null}
          </div>
          <p className="note" style={{ margin: '8px 0 0' }}>
            {empties.length
              ? `${empties.length} empty: ${empties.map((s) => s.key).join(', ')} — check that each is empty on purpose.`
              : 'Every declared key is set on this env.'}
          </p>
        </div>

        <div className="card pad">
          <h2>Cutover checklist · Talk to Sales (prod)</h2>
          <CutoverList state={ctx.cutover} onChange={ctx.setCut} />
          <p className="note" style={{ marginTop: 8 }}>
            Also on the webhook's Pipeline tab. All boxes ticked ⇒ the prod dry-run toggle unlocks.
          </p>
        </div>
      </section>

      {confirmLive ? (
        <Modal
          title="Send a LIVE probe?"
          onClose={() => setConfirmLive(false)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setConfirmLive(false)}>
                Cancel
              </button>
              <button type="button" className="btn danger" disabled={typed !== LIVE_WORD} onClick={() => void send()}>
                Send LIVE probe
              </button>
            </>
          }
        >
          <p className="note" style={{ margin: '0 0 8px' }}>
            The tts dial on <strong>{env}</strong> is live. This probe creates a real HubSpot contact and note for{' '}
            <code>revops-probe+…@optisigns.com</code>
            {form.requestType === 'sales'
              ? ' and enrolls it in the deal workflow — a rep gets a deal and a task to clean up.'
              : ' and sends a real email into the support inbox — a ticket gets created.'}
          </p>
          <label>
            Type <code>{LIVE_WORD}</code> to confirm
            <br />
            <input type="text" aria-label="confirm live probe" value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus />
          </label>
        </Modal>
      ) : null}
    </>
  );
}
